import React, { useContext, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { Box, Button, Grid, IconButton, Stack, useTheme, Typography, Tooltip } from "@mui/material"

import { persist, translateAssetPost, TranslatedAsset } from "src/services/api/translate"
import { UserContext, useGlobalLoader, useLogger, useUsageLimit } from "src/providers"
import { snackbarErrorHandler } from "src/utils/errorHandler"

import PageContainer from "src/components/page/PageContainer"
import TranslationResult from "./TranslationResult"
import UsageLimitDialog from "src/sites/file/UsageLimitDialog"

import {
  postAssetCreatedMetrics,
  postCopyMetrics,
  postTabletVisited,
  postTranslateMetrics
} from "src/services/api/metrics"
import LanguageSelect from "src/components/LanguageSelect"
import { SwapHoriz } from "@mui/icons-material"
import CustomTextarea from "./CustomTextarea"
import { appBarHeight, appBarHeightPixels, primary150 } from "src/providers/AppProviders"
import { useSnackbar } from "src/providers/SnackbarProvider"
import { useResponsive } from "src/utils/useResponsive"
import useLanguageManagement from "./useLanguageManagement"
import { Language } from "src/utils/useLanguageList"

const MAX_INPUT_LENGTH = 1500

// Custom hook for translation logic
const useTranslationLogic = () => {
  const { snackbarShowMessage } = useSnackbar()
  const { showLoader, hideLoader } = useGlobalLoader()
  const { logger } = useLogger()
  const { t } = useTranslation("common")
  const { checkUsageLimits } = useUsageLimit()
  const [translated, setTranslated] = useState<boolean>(false)
  const [creating, setCreating] = useState<boolean>(false)
  const [result, setResults] = useState<string>("")
  const [translatedAsset, setTranslatedAsset] = useState<TranslatedAsset>()
  const [resultModified, setResultModified] = useState<boolean>(false)

  const translate = async (text: string, langFrom: string, langTo: string, genre: string, taxonomy: string) => {
    try {
      showLoader()
      const translatedAsset = await translateAssetPost({
        langFrom,
        langTo,
        text: escapeCharacters(text),
        genre,
        taxonomy
      })
      const targetText = translatedAsset.targetText || ""
      setResults(xlfTokenRemover(targetText, t("translate.anonymized")))
      setTranslated(true)
      setTranslatedAsset(translatedAsset)
      postTranslateMetrics(text.length, translatedAsset.recycled || false, langFrom, langTo)

      // Check usage limits after translation to update the context
      setTimeout(async () => await checkUsageLimits(), 2000)
    } catch (e) {
      snackbarErrorHandler(e, { snackbarShowMessage }, t, () => {
        snackbarShowMessage(t("translate.error"), "error")
      })
      logger.error("User - Failed to translate")
    } finally {
      hideLoader()
    }
  }

  const createAsset = async (text: string, result: string, langFrom: string, langTo: string, taxonomy: string) => {
    try {
      setCreating(true)
      await persist({
        sourceText: text,
        targetText: result,
        langFrom,
        langTo,
        tokens: [],
        taxonomy,
        mtTargetText: translatedAsset?.targetText,
        engine: translatedAsset?.engine
      })
      postAssetCreatedMetrics(resultModified)
      snackbarShowMessage(t("translate.created"), "info")
    } catch (e) {
      snackbarErrorHandler(e, { snackbarShowMessage }, t, () => {
        snackbarShowMessage(t("translate.createError"), "error")
      })
      logger.error("User - Failed to create asset")
    } finally {
      setCreating(false)
    }
  }

  const goBack = () => {
    setTranslated(false)
    setTranslatedAsset(undefined)
    setResults("")
  }

  const onResultCopy = () => {
    postCopyMetrics(resultModified)
  }

  const onResultUpdate = (text: string) => {
    setResults(text)
    setResultModified(true)
  }

  return {
    translated,
    creating,
    result,
    translate,
    createAsset,
    goBack,
    onResultCopy,
    onResultUpdate
  }
}

// Language Selector Component
const LanguageSelector: React.FC<{
  langFrom: Language
  langTo: Language
  setLangFrom: (lang: Language) => void
  setLangTo: (lang: Language) => void
  translated: boolean
  text: string
  swap: () => void
  getSuggestionHelperText: () => string | undefined
  allUserLanguages: Language[]
}> = ({
  langFrom,
  langTo,
  setLangFrom,
  setLangTo,
  translated,
  text,
  swap,
  getSuggestionHelperText,
  allUserLanguages
}) => {
  const { t } = useTranslation("common")
  const { palette } = useTheme()
  const { isMobile } = useResponsive()

  const hintComponent = text && getSuggestionHelperText() && (
    <Typography variant="body2" color="text.secondary">
      {getSuggestionHelperText()}
    </Typography>
  )

  // todo: try to remove hintSpacer after proper layout is implemented
  const hintSpacer = (
    <Typography variant="body2" color="text.secondary">
      &nbsp;
    </Typography>
  )

  return (
    <Stack direction={"column"} spacing={{ xs: 2, md: 2 }} sx={{ width: "100%", height: "100%" }}>
      {isMobile && hintComponent}
      <Stack
        direction={"row"}
        justifyContent="center"
        alignItems="center"
        spacing={{ xs: 1, md: 2 }}
        sx={{
          width: "100%"
        }}
      >
        <Box
          sx={{
            flexBasis: { xs: "100%", md: "33%" },
            width: { xs: "100%", md: "auto" }
          }}
        >
          <LanguageSelect
            label={isMobile ? t("langFromMobile") : t("langFrom")}
            value={langFrom}
            disabled={translated}
            onChange={(e, val) => val && setLangFrom(val as Language)}
            options={allUserLanguages.filter((l) => l.languageId !== langTo.languageId)}
            data-testid="language-from-select"
          />
          {!isMobile && (hintComponent || hintSpacer)}
        </Box>

        {
          <Box sx={{ paddingTop: "9px" }}>
            <IconButton
              onClick={swap}
              disabled={translated}
              data-testid="swap-languages-button"
              sx={{
                borderRadius: "6px",
                backgroundColor: primary150,
                color: palette.midnightBlue.main,
                height: "auto",
                "&.Mui-disabled": {
                  backgroundColor: palette.action.disabledBackground
                },
                "&:hover": {
                  backgroundColor: palette.lightSkyBlue.main
                }
              }}
            >
              <SwapHoriz />
            </IconButton>
          </Box>
        }

        <Box
          sx={{
            flexBasis: { xs: "100%", md: "33%" },
            width: { xs: "100%", md: "auto" }
          }}
        >
          <LanguageSelect
            label={isMobile ? t("langToMobile") : t("langTo")}
            value={langTo}
            disabled={translated}
            onChange={(e, val) => val && setLangTo(val as Language)}
            options={allUserLanguages.filter((l) => l.languageId !== langFrom.languageId)}
            data-testid="language-to-select"
          />
          {!isMobile && hintSpacer}
        </Box>
      </Stack>
    </Stack>
  )
}

// Text Areas Component
const MobileTextAreas: React.FC<{
  text: string
  result: string
  translated: boolean
  onTextUpdate: (text: string) => void
  onResultUpdate: (text: string) => void
  onResultCopy: () => void
  setDisabledTranslation: (disabled: boolean) => void
}> = ({ text, result, translated, onTextUpdate, onResultUpdate, onResultCopy, setDisabledTranslation }) => {
  const textAreaRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null)
  const { logger } = useLogger()
  const [settingsLogged, setSettingsLogged] = useState(false)

  // calculate textarea rows as a workaround for mobile to fit the screen
  const footerHeight = 156.5
  const padding = 56
  const lineHeight = 23
  const rows = (window.innerHeight - appBarHeightPixels - footerHeight - padding) / lineHeight
  const resultRows = (window.innerHeight - appBarHeightPixels - footerHeight - padding * 2) / lineHeight / 2

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      textAreaRef.current?.focus()
    }, 100)
    return () => clearTimeout(timeoutId)
  }, [])

  return (
    <Stack direction={"column"} spacing={{ xs: 2, md: 2 }} sx={{ width: "100%", height: "100%" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "auto",
          minHeight: 0
        }}
      >
        {translated ? (
          <TranslationResult value={text} disabled data-testid="source-text-result" rows={resultRows} />
        ) : (
          <CustomTextarea
            inputRef={textAreaRef}
            value={text}
            clearable
            disabled={translated}
            onUpdate={onTextUpdate}
            onErrorChange={setDisabledTranslation}
            placeholder="Type or paste text to translate"
            maxInputLength={MAX_INPUT_LENGTH}
            data-testid="source-text-input"
            rows={rows}
            onFocus={() => {
              if (!settingsLogged) {
                logger.browserInfo()
                logger.info("Height debugging", {
                  height: window.innerHeight,
                  visualViewportHeight: visualViewport?.height,
                  documentElementHeight: document.documentElement.clientHeight
                })
                setSettingsLogged(true)
              }
            }}
          />
        )}
      </Box>

      {translated && (
        <Box
          sx={{
            flex: 1,
            minHeight: 0
          }}
        >
          <TranslationResult
            value={result}
            onModify={onResultUpdate}
            onCopy={onResultCopy}
            data-testid="target-text-result"
            rows={resultRows}
          />
        </Box>
      )}
    </Stack>
  )
}

// Text Areas Component
const TextAreas: React.FC<{
  text: string
  result: string
  translated: boolean
  onTextUpdate: (text: string) => void
  onResultUpdate: (text: string) => void
  onResultCopy: () => void
  setDisabledTranslation: (disabled: boolean) => void
}> = ({ text, result, translated, onTextUpdate, onResultUpdate, onResultCopy, setDisabledTranslation }) => {
  return (
    <Stack direction={{ md: "column", lg: "row" }} spacing={2} sx={{ width: "100%" }}>
      <Box
        sx={{
          flex: { md: "none", lg: 1 },
          width: "100%"
        }}
      >
        {translated ? (
          <TranslationResult value={text} disabled data-testid="source-text-result" rows={12} />
        ) : (
          <CustomTextarea
            rows={12}
            value={text}
            clearable
            disabled={translated}
            onUpdate={onTextUpdate}
            onErrorChange={setDisabledTranslation}
            placeholder="Type or paste text to translate"
            maxInputLength={MAX_INPUT_LENGTH}
            data-testid="source-text-input"
          />
        )}
      </Box>

      {translated && (
        <Box
          sx={{
            flex: { md: "none", lg: 1 },
            width: "100%"
          }}
        >
          <TranslationResult
            value={result}
            onModify={onResultUpdate}
            onCopy={onResultCopy}
            data-testid="target-text-result"
            rows={12}
          />
        </Box>
      )}
    </Stack>
  )
}

// Action Buttons Component
const ActionButtons: React.FC<{
  translated: boolean
  creating: boolean
  text: string
  disabledTranslation: boolean
  langsSelected: boolean
  langFrom: Language
  langTo: Language
  isMobile: boolean
  onTranslate: () => void
  onGoBack: () => void
  onCreateAsset: () => void
}> = ({
  translated,
  creating,
  text,
  disabledTranslation,
  langsSelected,
  langFrom,
  langTo,
  isMobile,
  onTranslate,
  onGoBack,
  onCreateAsset
}) => {
  const { t } = useTranslation("common")
  const { isTranslateAccessRestricted } = useUsageLimit()

  const sameLangSelected = langFrom.languageId === langTo.languageId && !!langFrom && !!langTo
  const isTranslateDisabled =
    !text || disabledTranslation || !langsSelected || isTranslateAccessRestricted() || sameLangSelected

  const getTooltipMessage = () => {
    if (sameLangSelected) {
      return "Source and target languages cannot be the same"
    }
    if (!text) {
      return "Please enter text to translate"
    }
    if (!langsSelected) {
      return "Please select both source and target languages"
    }
    return ""
  }

  return (
    <Stack
      direction={"row"}
      justifyContent="center"
      alignItems="center"
      spacing={{ xs: 1, md: 2 }}
      sx={{ width: "100%", pb: 2 }}
    >
      {translated && (
        <Button variant="contained" onClick={onGoBack} disableElevation data-testid="back-button" fullWidth={isMobile}>
          {t("translate.back")}
        </Button>
      )}
      {translated && (
        <Button
          variant="outlined"
          onClick={onCreateAsset}
          disabled={creating}
          data-testid="create-asset-button"
          fullWidth={isMobile}
        >
          {t("translate.createNewAsset")}
        </Button>
      )}
      {!translated && (
        <Tooltip title={getTooltipMessage()} arrow placement="top">
          <span>
            <Button
              variant="contained"
              color="primary"
              disabled={isTranslateDisabled}
              onClick={onTranslate}
              data-testid="translate-button"
            >
              {t("translate.title")}
            </Button>
          </span>
        </Tooltip>
      )}
    </Stack>
  )
}

const AiTextTranslatorPage = () => {
  const { companyPortalProperties } = useContext(UserContext)
  const [text, setText] = useState<string>("")
  const [disabledTranslation, setDisabledTranslation] = useState<boolean>(false)

  const { isMobile } = useResponsive()

  // Custom hooks
  const { translated, creating, result, translate, createAsset, goBack, onResultCopy, onResultUpdate } =
    useTranslationLogic()

  const {
    langFrom,
    setLangFrom,
    langTo,
    setLangTo,
    supported,
    checkEngineSupport,
    startDetectionDelay,
    swap,
    langsSelected,
    getSuggestionHelperText,
    allUserLanguages
  } = useLanguageManagement()

  useEffect(() => {
    postTabletVisited("TRANSLATE_AND_CREATE")
  }, [])

  const doTranslate = () => {
    if (!text || !supported) return
    checkEngineSupport()
    translate(
      text,
      langFrom.languageId,
      langTo.languageId,
      companyPortalProperties.genre,
      companyPortalProperties.taxonomy
    )
  }

  const onTextUpdate = (text: string) => {
    setText(text)
    startDetectionDelay(text)
    if (text === "") onResultUpdate("")
  }

  const handleCreateAsset = () => {
    createAsset(text, result, langFrom.languageId, langTo.languageId, companyPortalProperties.taxonomy)
  }

  const handleBack = () => {
    goBack()
    setText("")
  }

  const languageSelector = (
    <LanguageSelector
      langFrom={langFrom}
      langTo={langTo}
      setLangFrom={setLangFrom}
      setLangTo={setLangTo}
      translated={translated}
      text={text}
      swap={swap}
      getSuggestionHelperText={getSuggestionHelperText}
      allUserLanguages={allUserLanguages}
    />
  )

  const textAreas = (
    <TextAreas
      text={text}
      result={result}
      translated={translated}
      onTextUpdate={onTextUpdate}
      onResultUpdate={onResultUpdate}
      onResultCopy={onResultCopy}
      setDisabledTranslation={setDisabledTranslation}
    />
  )

  const actionButtons = (
    <ActionButtons
      translated={translated}
      creating={creating}
      text={text}
      disabledTranslation={disabledTranslation}
      langsSelected={langsSelected()}
      langFrom={langFrom}
      langTo={langTo}
      isMobile={isMobile}
      onTranslate={doTranslate}
      onGoBack={handleBack}
      onCreateAsset={handleCreateAsset}
    />
  )

  if (isMobile) {
    return (
      <PageContainer>
        <Box sx={{ display: "flex", flexDirection: "column", height: `calc(100dvh - ${appBarHeight})` }}>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <MobileTextAreas
              text={text}
              result={result}
              translated={translated}
              onTextUpdate={onTextUpdate}
              onResultUpdate={onResultUpdate}
              onResultCopy={onResultCopy}
              setDisabledTranslation={setDisabledTranslation}
            />
          </Box>
          <Box
            sx={{
              flexGrow: 0,
              px: 2,
              py: 3,
              boxShadow: "0px -5px 15px #0719391A",
              zIndex: 1 // box shadow workaround
            }}
          >
            <Stack direction="column" spacing={2} alignItems="center">
              {languageSelector}
              {actionButtons}
            </Stack>
          </Box>
        </Box>
        <UsageLimitDialog pageName="text" />
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <Grid
        item
        xs={12}
        sx={{
          p: { xs: 2, md: 6, lg: 8 }
        }}
      >
        <Stack spacing={{ xs: 2, md: 4 }}>
          {languageSelector}
          {textAreas}
          {actionButtons}
        </Stack>
      </Grid>
      <UsageLimitDialog pageName="text" />
    </PageContainer>
  )
}

export default AiTextTranslatorPage

function xlfTokenRemover(text: string, replaceText = "[ANONYMIZED] "): string {
  const TOKEN_UUID_PATTERN = /XFL_TOKEN_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gm
  return text.replaceAll(TOKEN_UUID_PATTERN, replaceText)
}

export function escapeCharacters(text: string, revert = false) {
  const escapeCharacters = new Map()
  escapeCharacters
    .set("[", "%5B")
    .set("]", "%5D")
    .set("<", "%3C")
    .set(">", "%3E")
    .set("{", "%7B")
    .set("}", "%7D")
    .set("$", "%24")
    .set("&", "%26")
    .set("?", "%3F")
    .set("#", "%23")

  return Array.from(escapeCharacters.entries()).reduce((acc, [k, v]) => {
    return revert ? acc.replaceAll(v, k) : acc.replaceAll(k, v)
  }, text)
}
